import {gql} from '@apollo/client';
import {CourseMediaAssetsContext} from './CourseMediaAssetsContext';
import {resolveVideoSource, mediaSourceUrl} from '../media/MediaPlayer';
import { showToast } from '../../utils/toast.util';
import * as React from 'react';
import {DirectUpload} from '../media/DirectUpload';
import {FileProps} from '../../types';
import {MediaPreviewComponent} from '../media/MediaPreview';
import {VideoFrameSelector} from './_components/video-frame-selector';

type HandleMediaUpdateProps = {
  (mediaType: 'cover' | 'poster' | 'video' | 'audio', url: string): void;
};

type UnitMediaComponentProps = {
  handleMediaUpdate: HandleMediaUpdateProps;
  alternativeSrc?: string;
  coverUrl?: string;
  posterUrl?: string;
  videoUrl?: string;
  audioUrl?: string;
  cover?: FileProps;
  poster?: FileProps;
  video?: FileProps;
  audio?: FileProps;
  unit: any;
};

const REQUEST_PRESIGNED_UPLOAD = gql`
  mutation RequestPresignedUpload(
    $filename: String!
    $contentType: String!
    $folder: String
  ) {
    requestPresignedUpload(
      input: {filename: $filename, contentType: $contentType, folder: $folder}
    ) {
      presignedUpload {
        url
        fields
        key
        fileUrl
      }
    }
  }
`;

export function resolveCoverUploadUrl(url: string): string {
  return process.env.NODE_ENV !== 'production'
    ? url.replace('localstack:4566', 'localhost:4566')
    : url;
}

export const UnitMediaComponent = ({
  handleMediaUpdate,
  ...props
}: UnitMediaComponentProps): JSX.Element => {
  const [showVideoFrameSelector, setShowVideoFrameSelector] =
    React.useState(false);

  const assets = React.useContext(CourseMediaAssetsContext);
  const videoSource = resolveVideoSource(props.videoUrl || props.video);
  const hasVideoUrl = Boolean(videoSource.url);
  const canCaptureFrame = videoSource.kind === 'native';
  const poster = mediaSourceUrl(props.posterUrl || props.poster || props.coverUrl || props.cover) || assets.posterPlaceholder;

  const handleVideoFrameSelected = async (frameDataUrl: string) => {
    try {
      const response = await fetch(frameDataUrl);
      const blob = await response.blob();
      const file = new File([blob], 'video_cover.jpg', {type: 'image/jpeg'});

      const {client} = await import('../../../core/stores');

      const {data} = await client('account').mutate({
        mutation: REQUEST_PRESIGNED_UPLOAD,
        variables: {
          filename: file.name,
          contentType: file.type,
          folder: 'units/covers',
        },
      });

      const presignedData = data.requestPresignedUpload.presignedUpload;

      const uploadUrl = resolveCoverUploadUrl(presignedData.url);

      const xhr = new XMLHttpRequest();

      await new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {

            resolve(xhr.response);
          } else {
            reject(new Error(`Falha no envio: ${xhr.status} ${xhr.statusText}`));
          }
        };

        xhr.onerror = () => reject(new Error('Erro de conexão'));

        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);

        if (presignedData.fields && typeof presignedData.fields === 'object') {
          Object.entries(presignedData.fields).forEach(
            ([headerName, headerValue]) => {
              if (headerValue !== null && headerValue !== undefined) {
                xhr.setRequestHeader(headerName, headerValue.toString());
              }
            },
          );
        }

        xhr.send(file);
      });

      handleMediaUpdate('cover', presignedData.fileUrl);

      setShowVideoFrameSelector(false);

      showToast({html: 'Capa do vídeo criada com sucesso!'});
    } catch (error) {
      showToast({
        html: 'Erro ao fazer upload da capa do vídeo: ' + (error instanceof Error ? error.message : 'Erro desconhecido'),
      });
    }
  };
  return (
    <div className="row">
      <div className="col s12">
        <div className="flow-text">Mídia da aula</div>
      </div>
      <div className="row">
        <div className="col s12 m6">
          <DirectUpload
            mediaType="video"
            folder="units/videos"
            onUploadComplete={(url) => handleMediaUpdate('video', url)}>
            <MediaPreviewComponent
              mediaType="video"
              lessonCard
              title="Vídeo da aula"
              poster={poster}
              alternativeSrc={props.videoUrl && props.videoUrl !== props.unit.video?.uri ? undefined : props.alternativeSrc}
              file={props.video}
              removeAll={() => handleMediaUpdate('video', '')}
              isUploading={false}
              uploadProgress={0}
            />
          </DirectUpload>
        </div>
        <div className="col s12 m6">
          <DirectUpload
            mediaType="audio"
            folder="units/audios"
            onUploadComplete={(url) => handleMediaUpdate('audio', url)}>
            <MediaPreviewComponent
              mediaType="audio"
              lessonCard
              title="Áudio da aula"
              poster={assets.audioCover}
              file={props.audio}
              removeAll={() => handleMediaUpdate('audio', '')}
              isUploading={false}
              uploadProgress={0}
            />
          </DirectUpload>
        </div>
        <div className="col s12 m6">
          <div className="flow-text">Capa</div>
          <DirectUpload
            mediaType="image"
            folder="units/covers"
            onUploadComplete={(url) => handleMediaUpdate('cover', url)}>
            <MediaPreviewComponent
              mediaType="image"
              lessonCard
              title="Capa da aula"
              file={props.cover}
              removeAll={() => handleMediaUpdate('cover', '')}
              isUploading={false}
              uploadProgress={0}
            />
          </DirectUpload>

          {hasVideoUrl && (
            <div style={{marginTop: '10px'}}>
              <button
                type="button"
                className="btn waves-effect waves-light blue lighten-2"
                disabled={!canCaptureFrame}
                aria-label="Criar capa a partir do vídeo"
                onClick={() => setShowVideoFrameSelector(true)}
                style={{width: '100%'}}>
                <i className="material-icons left">video_library</i>
                Criar capa do vídeo
              </button>
              {!canCaptureFrame && <p>Para vídeos HLS ou Vimeo, envie uma imagem de capa.</p>}
            </div>
          )}
        </div>
        <div className="col s12 m6">
          <div className="flow-text">Pôster</div>
          <DirectUpload
            mediaType="image"
            folder="units/posters"
            onUploadComplete={(url) => handleMediaUpdate('poster', url)}>
            <MediaPreviewComponent
              mediaType="image"
              lessonCard
              title="Pôster da aula"
              file={props.poster}
              removeAll={() => handleMediaUpdate('poster', '')}
              isUploading={false}
              uploadProgress={0}
            />
          </DirectUpload>
        </div>
      </div>

      {showVideoFrameSelector && canCaptureFrame && (
        <VideoFrameSelector
          videoUrl={props.videoUrl || props.video?.preview || ''}
          onFrameSelected={handleVideoFrameSelected}
          onClose={() => setShowVideoFrameSelector(false)}
        />
      )}
    </div>
  );
};
